// useOfflineStatus — subscribes to navigator online/offline transitions
// and the sync-drainer's queue + lifecycle events. The OfflineIndicator
// component renders from this state.
//
// We re-query the queue depth from IndexedDB on every PENDING_EVENT so
// we don't have to mirror state — the source of truth is the IDB store
// itself.

import { useEffect, useState } from "preact/hooks";
import { localStore } from "./local-store";
import { isDraining, SYNC_DRAINED_EVENT, SYNC_DRAINING_EVENT } from "./sync-drainer";

export interface OfflineStatus {
  /** navigator.onLine + a soft "we just had a network failure" hint.
   *  For now we trust the OS — Phase D could add a probe ping. */
  online: boolean;
  /** Pending writes waiting to be replayed. */
  queueDepth: number;
  /** True while the drainer is actively replaying. */
  draining: boolean;
}

export function useOfflineStatus(): OfflineStatus {
  const [online, setOnline] = useState<boolean>(() =>
    typeof navigator !== "undefined" ? navigator.onLine !== false : true);
  const [queueDepth, setQueueDepth] = useState<number>(0);
  const [draining, setDraining] = useState<boolean>(() => isDraining());

  useEffect(() => {
    // Pull current queue depth once on mount. Subsequent updates come
    // from the PENDING_EVENT dispatches inside local-store + drainer.
    const sync = () => {
      localStore.listPending()
        .then((rows) => setQueueDepth(rows.length))
        .catch(() => setQueueDepth(0));
    };
    sync();

    const onOnline  = () => setOnline(true);
    const onOffline = () => setOnline(false);
    const onPendingChanged = () => sync();
    const onDrainStart = () => setDraining(true);
    const onDrainEnd   = () => { setDraining(false); sync(); };

    window.addEventListener("online", onOnline);
    window.addEventListener("offline", onOffline);
    window.addEventListener(localStore.PENDING_EVENT, onPendingChanged);
    window.addEventListener(SYNC_DRAINING_EVENT, onDrainStart);
    window.addEventListener(SYNC_DRAINED_EVENT, onDrainEnd);

    return () => {
      window.removeEventListener("online", onOnline);
      window.removeEventListener("offline", onOffline);
      window.removeEventListener(localStore.PENDING_EVENT, onPendingChanged);
      window.removeEventListener(SYNC_DRAINING_EVENT, onDrainStart);
      window.removeEventListener(SYNC_DRAINED_EVENT, onDrainEnd);
    };
  }, []);

  return { online, queueDepth, draining };
}
